import React, { useEffect, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import step1 from '../assets/Step1.png';
import step2 from '../assets/Step2.png';
import step3 from '../assets/Step3.png';
import step4 from '../assets/Step4.png';
import step5 from '../assets/Step5.png';
import step6 from '../assets/Step6.png';

const originalData = [
   {
      title: 'Step 1',
      head: 'Seal & Secure',
      desc: `<strong>Handling recyclable materials: Recycling Guidelines for Cans, Plastics, and Glass:</strong><br/>
✔ Use the clear bags provided to bag all recycling materials.<br/>
✔ Sort items into 3 separate types - Aluminum cans, Plastic bottles, Glass (non-broken)<br/>
✔ Drain all liquid from containers before bagging.<br/>
✔ Keep cans separate from plastics.<br/>
✔ Do not mix cans and plastics in the same bag.<br/>
✔ Avoid partial bag loads; please fill bags completely with only one type of material.<br/>
<strong>For Glass:</strong><br/>
✔ Bags containing glass should NOT be filled to the top to prevent tearing.<br/>
✔ We strongly recommend double-bagging your glass.<br/>
✔ Fill bags with approximately 20 lbs of non-broken glass (about 1/4 full).<br/>
✔ Ensure glass is not broken to avoid injury and bag damage.<br/>`,
      img: step1,
   },
   {
      title: 'Step 2',
      head: 'Bag & Tag',
      desc: `<strong>How to Ensure Your Bags Are Securely Tied for Pickup:</strong><br/>
✔ Use Durable Bags<br/>
Choose strong, heavy-duty garbage or yard waste bags to prevent rips and leaks.<br/>
✔ Don’t Overfill<br/>
Leave space at the top to tie the bag easily.<br/>
Avoid sharp or heavy items that could tear the bag.<br/>
✔ Tie the Bag Securely<br/>
Twist the top tightly and use a double knot, twist tie, zip tie, or clip.<br/>
✔ Test the Knot<br/>
Gently pull to make sure the knot is secure and won’t come undone.<br/>
✔ Label if Necessary<br/>
If the bag contains special waste (e.g. yard waste), label it clearly.<br/>
✔ Place in a Visible Area<br/>
Put bags at the curb or in an open, accessible spot.<br/>
Do not place behind gates, fences, or inside garages.`,
      img: step2,
   },
   {
      title: 'Step 3',
      head: 'Label It Up',
      desc: `<strong>To comply with the instructions for your recycling program: Adding a Label</strong><br/>
               ✔ Affix one provided Eco Recycle QR code label on each of your bags in an easy-to-see location.<br/>
               ✔ When you schedule a pick-up, upload a clear, close-up photo of the QR code on each bag.<br/>
               ✔ This ensures proper identification and helps facilitate efficient collection.<br/>
               ✔ QR codes provide access to recycling info and help track recycling efforts.<br/>
               ✔ These labels often link to real-time, location-specific instructions, improving recycling accuracy.<br/>
               ✔ Make sure the QR code is clearly visible, and the photo is sharp and readable.<br/>
               ✔ This helps the recycling program verify your bags and streamline the pick-up process.<br/>`,
      img: step3,
   },
   {
      title: 'Step 4',
      head: 'Book a Pickup',
      desc: `<strong>To schedule a pick-up through our website, follow these steps:</strong><br/>
               ✔ Log in to your account on our website.<br/>
               ✔ Use our online scheduler to book your pick-up.<br/>
               ✔ Upload individual photos of each bag, ensuring the bar code label is clearly visible.<br/>
               ✔ Indicate the total number of bags you are scheduling for pick-up.<br/>
               ✔ Select your preferred pick-up time from the available options in your neighborhood.<br/>
               ✔ If you have specific instructions for our drivers, include them in the comments section.<br/>
               &nbsp;&nbsp;This may include gate codes, exact bag location, or requests for additional bags or labels.<br/>`,
      img: step4,
   },
   {
      title: 'Step 5',
      head: 'Pickup Confirmed & Collected',
      desc: `✔ Once your bags have been picked up from your location, you’ll receive a notification via text or email confirming collection.<br/>
   ✔ This confirms your bags are being transported to our processing facility.<br/>
   ✔ Delivery to the processing center usually occurs within 1–2 days after pick-up.<br/>
   ✔ Timeline may vary depending on your location and the logistics provider's schedule.<br/>
   ✔ For the most accurate ETA, refer to your tracking notification or contact customer support.<br/>`,
      img: step5,
   },
   {
      title: 'Step 6',
      head: 'Rewards Credited',
      desc: `✔ Your rewards are calculated based on the number and type of eligible beverage containers returned.<br/>

<strong>For New York (NY):</strong><br/>
✔ Earn 5¢ in rewards per eligible container.<br/>
✔ Containers must have a valid NY deposit label.<br/>
✔ Standard limit: 240 containers per person/day unless pre-arranged.<br/>

<strong>For Maine (ME):</strong><br/>
✔ Most containers earn 5¢ in rewards; select items may earn higher value.<br/>
✔ Total rewards are calculated based on applicable deposit values.<br/>

<strong>Example:</strong><br/>
✔ 100 containers = $5.00 in rewards credits<br/>

<strong>Note:</strong><br/>
✔ Rewards are issued as credits within your account and can be redeemed within the platform.`,
      img: step6,
   }
];

export default function RecycleSlider() {
   const [visibleSlides, setVisibleSlides] = useState(1);
   const [currentIndex, setCurrentIndex] = useState(0);
   const [transitioning, setTransitioning] = useState(true);
   const [clonedData, setClonedData] = useState([]);
   const sliderRef = useRef();

   useEffect(() => {
      const updateVisibleSlides = () => {
         if (window.innerWidth < 768) {
            setVisibleSlides(1); // Mobile & small tablets
         } else {
            setVisibleSlides(2); // Desktops & larger tablets
         }
      };
      updateVisibleSlides();
      window.addEventListener('resize', updateVisibleSlides);
      return () => window.removeEventListener('resize', updateVisibleSlides);
   }, []);

   useEffect(() => {
      const prepend = originalData.slice(-visibleSlides);
      const append = originalData.slice(0, visibleSlides);
      setClonedData([...prepend, ...originalData, ...append]);
      setCurrentIndex(visibleSlides); // Start at first real slide
   }, [visibleSlides]);

   const slideWidth = 100 / clonedData.length;

   const handleNext = () => {
      setTransitioning(true);
      setCurrentIndex((prev) => prev + 1);
   };

   const handlePrev = () => {
      setTransitioning(true);
      setCurrentIndex((prev) => prev - 1);
   };

   const handleTransitionEnd = () => {
      if (currentIndex === clonedData.length - visibleSlides) {
         setTransitioning(false);
         setCurrentIndex(visibleSlides);
      }
      if (currentIndex === 0) {
         setTransitioning(false);
         setCurrentIndex(originalData.length);
      }
   };

   const goToIndex = (i) => {
      setTransitioning(true);
      setCurrentIndex(i + visibleSlides);
   };

   return (
      <div className="bg-[#ECFFDC] py-10 overflow-hidden relative">
         <div className="max-w-7xl mx-auto px-4">
            <div className="relative overflow-hidden">
               <h2 className="text-3xl font-bold text-[#1a4632] text-center mb-6">
                  How Our
                  <span className="text-[#9cc026]"> Recycling Process</span> Works
               </h2>

               <div
                  ref={sliderRef}
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{
                     width: `${(100 / visibleSlides) * clonedData.length}%`,
                     transform: `translateX(-${(100 / clonedData.length) * currentIndex}%)`,
                     transition: transitioning ? 'transform 0.5s ease' : 'none',
                  }}
                  onTransitionEnd={handleTransitionEnd}
               >
                  {clonedData.map((card, index) => (
                     <div
                        key={index}
                        style={{ flex: `0 0 ${100 / clonedData.length}%` }}
                        className="p-6 flex"
                     >
                        <div className="bg-white rounded-3xl p-8 flex flex-col items-stretch gap-6 transition-transform duration-500 hover:scale-[1.03] border border-[#e0f0c9] w-full">

                           {/* Image */}
                           <div className="w-full">
                              <img
                                 src={card.img}
                                 alt={card.title}
                                 className="w-full md:h-64 lg:h-80 object-contain rounded-lg"
                              />
                           </div>

                           {/* Text */}
                           <div className="w-full flex flex-col justify-start">
                              <span className="inline-block bg-[#e9f7e6] text-[#1a4632] px-5 py-2 rounded-full text-xl font-semibold mb-3 break-words">
                                 {card.title}
                              </span>
                              <p className="text-[#9cc026] text-xl font-semibold mb-3 break-words">
                                 {card.head}
                              </p>
                              <div
                                 className="text-gray-700 text-base leading-relaxed"
                                 style={{ textAlign: 'justify' }}
                                 dangerouslySetInnerHTML={{ __html: card.desc }}
                              />
                           </div>

                        </div>
                     </div>
                  ))}
               </div>

               {/* Arrows */}
               <button
                  onClick={handlePrev}
                  className="absolute top-1/2 left-0 transform -translate-y-1/2 p-4 rounded-full z-10 hover:bg-[#d9f3a6]"
                  aria-label="Previous Slide"
               >
                  <FaChevronLeft className="text-[#74AF20] text-3xl" />
               </button>

               <button
                  onClick={handleNext}
                  className="absolute top-1/2 right-0 transform -translate-y-1/2 p-4 rounded-full z-10 hover:bg-[#d9f3a6]"
                  aria-label="Next Slide"
               >
                  <FaChevronRight className="text-[#74AF20] text-3xl" />
               </button>
            </div>

            {/* Dots */}
            <div className="mt-6 flex justify-center gap-3">
               {originalData.map((_, i) => (
                  <button
                     key={i}
                     onClick={() => goToIndex(i)}
                     className={`w-2 h-2 rounded-full ${currentIndex - visibleSlides === i ? 'bg-[#74AF20]' : 'bg-gray-300'
                        } transition-all duration-300`}
                     aria-label={`Go to slide ${i + 1}`}
                  />
               ))}
            </div>
         </div>
      </div>
   );

}
